import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

// ISO 3166-1 alpha-2 country codes with English display names.
// Stores the raw two-letter code (e.g. "US", "SG") into the form value —
// same shape the previous free-text <Input> produced. Legacy records
// whose stored country is not in this list still render as-is in the
// trigger button; the dropdown only offers the curated ISO-2 set.
export const ISO_COUNTRIES = [
  ["AE", "United Arab Emirates"], ["AR", "Argentina"], ["AT", "Austria"],
  ["AU", "Australia"], ["BD", "Bangladesh"], ["BE", "Belgium"], ["BH", "Bahrain"],
  ["BR", "Brazil"], ["CA", "Canada"], ["CH", "Switzerland"], ["CL", "Chile"],
  ["CN", "China"], ["CO", "Colombia"], ["CY", "Cyprus"], ["CZ", "Czechia"],
  ["DE", "Germany"], ["DK", "Denmark"], ["EG", "Egypt"], ["ES", "Spain"],
  ["FI", "Finland"], ["FR", "France"], ["GB", "United Kingdom"], ["GR", "Greece"],
  ["HK", "Hong Kong"], ["HU", "Hungary"], ["ID", "Indonesia"], ["IE", "Ireland"],
  ["IL", "Israel"], ["IN", "India"], ["IS", "Iceland"], ["IT", "Italy"],
  ["JP", "Japan"], ["KE", "Kenya"], ["KH", "Cambodia"], ["KR", "South Korea"],
  ["KW", "Kuwait"], ["KY", "Cayman Islands"], ["LK", "Sri Lanka"], ["LU", "Luxembourg"],
  ["MA", "Morocco"], ["MO", "Macau"], ["MT", "Malta"], ["MU", "Mauritius"],
  ["MX", "Mexico"], ["MY", "Malaysia"], ["NG", "Nigeria"], ["NL", "Netherlands"],
  ["NO", "Norway"], ["NZ", "New Zealand"], ["OM", "Oman"], ["PA", "Panama"],
  ["PE", "Peru"], ["PH", "Philippines"], ["PK", "Pakistan"], ["PL", "Poland"],
  ["PT", "Portugal"], ["QA", "Qatar"], ["RO", "Romania"], ["RU", "Russia"],
  ["SA", "Saudi Arabia"], ["SE", "Sweden"], ["SG", "Singapore"], ["TH", "Thailand"],
  ["TR", "Türkiye"], ["TW", "Taiwan"], ["UA", "Ukraine"], ["US", "United States"],
  ["VG", "British Virgin Islands"], ["VN", "Vietnam"], ["ZA", "South Africa"],
];

const CODE_TO_NAME = Object.fromEntries(ISO_COUNTRIES);

export function CountryCombobox({ value, onChange, testId, invalid = false }) {
  const [open, setOpen] = useState(false);
  const label = value ? (CODE_TO_NAME[value] ? `${value} — ${CODE_TO_NAME[value]}` : value) : "";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between bg-secondary/50 font-normal ${invalid ? "border-red-500/50" : ""}`}
          data-testid={testId}
        >
          <span className={label ? "" : "text-muted-foreground"}>
            {label || "Select country"}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search country..." data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {ISO_COUNTRIES.map(([code, name]) => (
                <CommandItem
                  key={code}
                  value={`${code} ${name}`}
                  onSelect={() => { onChange(code); setOpen(false); }}
                  data-testid={`${testId}-option-${code}`}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === code ? "opacity-100" : "opacity-0"}`} />
                  <span className="font-mono text-xs w-8">{code}</span>
                  <span className="ml-2">{name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default CountryCombobox;
